import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  TablePagination,
} from '@mui/material';

import { Add, Edit, Delete, Visibility as ViewIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../services/api';
import { staffAPI } from '../../services/staffMember';
import type { StaffMember } from '../../services/staffMember';

interface StaffFormData {
  name: string;
  email: string;
  phone: string;
  sp_country_code: string;
  password: string;
  status: 'active' | 'inactive' | 'suspended';
  is_top_cleaner: boolean;
}

const StaffManagement: React.FC = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  
  const [countries, setCountries] = useState<Array<{code: string; name: string; phoneCode: string}>>([]);
  const [formData, setFormData] = useState<StaffFormData>({
    name: '',
    email: '',
    phone: '',
    sp_country_code: '+32',
    password: '',
    status: 'active',
    is_top_cleaner: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const ucfirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchStaff = async (currentPage: number = page, currentRowsPerPage: number = rowsPerPage) => {
    try {
      const apiPage = currentPage + 1; // API is 1-based
      const result = await staffAPI.getAll(apiPage, currentRowsPerPage);
      setStaff(result.staff);
      setTotalItems(result.total_records);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showSnackbar('Failed to fetch staff members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/country/list`);
      if (!response.ok) throw new Error('Failed to fetch countries');
      const result = await response.json();
      
      if (result.status === 'success' && Array.isArray(result.data)) {
        // Create a map of the preferred country codes for easy lookup
        const preferredCodes = ['32', '33', '49'];
        const preferredCountries: Array<{code: string; name: string; phoneCode: string}> = [];
        const otherCountries: Array<{code: string; name: string; phoneCode: string}> = [];
        
        // Process all countries
        result.data.forEach((country: any) => {
          const countryItem = {
            code: country.country_code,
            name: country.country_name,
            phoneCode: country.phone_code.replace('+', '')
          };
          
          // Check if this is one of our preferred countries
          if (preferredCodes.includes(countryItem.phoneCode)) {
            preferredCountries.push(countryItem);
          } else {
            otherCountries.push(countryItem);
          }
        });
        
        // Sort preferred countries in the order: 32, 33, 49
        const sortedPreferred = preferredCountries.sort((a, b) => {
          const order: Record<string, number> = { '32': 1, '33': 2, '49': 3 };
          return (order[a.phoneCode] || 0) - (order[b.phoneCode] || 0);
        });
        
        // Sort other countries alphabetically
        const sortedOther = otherCountries.sort((a, b) => a.name.localeCompare(b.name));
        
        // Combine with preferred countries first
        setCountries([...sortedPreferred, ...sortedOther]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Fallback with preferred countries first
      setCountries([
        { code: 'BE', name: 'Belgium', phoneCode: '32' },
        { code: 'FR', name: 'France', phoneCode: '33' },
        { code: 'DE', name: 'Germany', phoneCode: '49' },
        { code: 'IN', name: 'India', phoneCode: '91' },
        { code: 'US', name: 'United States', phoneCode: '1' },
        { code: 'GB', name: 'United Kingdom', phoneCode: '44' },
        { code: 'AE', name: 'United Arab Emirates', phoneCode: '971' }
      ]);
      // Set default country code to Belgium (+32)
      setFormData(prev => ({
        ...prev,
        sp_country_code: '+32'
      }));
    }
  };

  useEffect(() => {
    fetchStaff(page, rowsPerPage);
    fetchCountries();
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value as 'active' | 'inactive' | 'suspended'
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phone || !formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    // Only validate password for new staff members
    if (!editingStaff && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      // Prepare base data
      const baseData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        sp_country_code: formData.sp_country_code,
        status: formData.status,
        is_top_cleaner: formData.is_top_cleaner,
      };
      
      if (editingStaff) {
        // Check if we have a valid ID for updating
        if (!editingStaff.id) {
          showSnackbar('Cannot update: Invalid staff member ID', 'error');
          return;
        }
        
        // For updates, password is optional
        const updateData = {
          ...baseData,
          ...(formData.password && { password: formData.password }),
        };
        
        await staffAPI.update(editingStaff.id, updateData);
        showSnackbar('Staff member updated successfully', 'success');
      } else {
        // For creation, password is required
        if (!formData.password) {
          showSnackbar('Password is required for new staff', 'error');
          return;
        }
        
        const createData = {
          ...baseData,
          password: formData.password, // Required for creation
          user_type: 'staff' as const
        };
        
        console.log('Creating staff with data:', createData);
        await staffAPI.create(createData);
        showSnackbar('Staff member added successfully', 'success');
      }
      
      setOpenDialog(false);
      fetchStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to save staff member',
        'error'
      );
    }
  };

  const handleDelete = async (id: number | undefined) => {
    if (!id) {
      showSnackbar('Cannot delete: Invalid staff member ID', 'error');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await staffAPI.delete(id);
        showSnackbar('Staff member deleted successfully', 'success');
        fetchStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
        showSnackbar('Failed to delete staff member', 'error');
      }
    }
  };

  const handleEdit = (staffMember: StaffMember) => {
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone || staffMember.mobile || '',
      sp_country_code: staffMember.sp_country_code || staffMember.country_code || '+32',
      password: '',
      status: staffMember.status,
      is_top_cleaner: staffMember.is_top_cleaner === 'Yes' || staffMember.is_top_cleaner === true,
    });
    setFormErrors({});
    setEditingStaff(staffMember);
    setOpenDialog(true);
  };

  const handleAddNew = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      sp_country_code: '+32',
      password: '',
      status: 'active',
      is_top_cleaner: false
    });
    setFormErrors({});
    setEditingStaff(null);
    setOpenDialog(true);
  };

  return (
    <Box p={3}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>Washer Management</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Add />}
          onClick={handleAddNew}
        >
          Add Washer
        </Button>
      </Box>

      
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow key="loading">
                <TableCell colSpan={5} align="center">Loading...</TableCell>
              </TableRow>
            ) : staff.length === 0 ? (
              <TableRow key="no-data">
                <TableCell colSpan={5} align="center">No staff members found</TableCell>
              </TableRow>
            ) : (
              staff.map((member) => (
                <TableRow key={member.id || `staff-${member.email}`}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.phone}</TableCell>
                  <TableCell>
                    <Chip 
                      label={member.status && ucfirst(member.status)}
                      color={member.status === 'active' ? 'success' : 'default'} 
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                        component={Link}
                        to={`/washers/${member.id}`}
                        color="primary"
                        size="small"
                        title="View Washer Details"
                        >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    <IconButton 
                      onClick={() => handleEdit(member)} 
                      color="primary"
                      disabled={!member.id}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(member.id)} 
                      color="error"
                      disabled={!member.id}
                    >
                      <Delete />
                    </IconButton>
                    
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStaff ? 'Edit Washer' : 'Add New Washer'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              margin="normal"
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ width: '30%' }} margin="normal">
                <InputLabel>Country Code</InputLabel>
                <Select
                  name="sp_country_code"
                  value={formData.sp_country_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, sp_country_code: e.target.value }))}
                  label="Country Code"
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={`+${country.phoneCode}`}>
                      {country.name} (+{country.phoneCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                margin="normal"
                required
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required={!editingStaff}
              error={!!formErrors.password}
              helperText={formErrors.password || (editingStaff ? 'Leave blank to keep current password' : '')}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Top Cleaner</InputLabel>
              <Select
                name="is_top_cleaner"
                value={formData.is_top_cleaner ? 'Yes' : 'No'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  is_top_cleaner: e.target.value === 'Yes'
                }))}
                label="Top cleaner"
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleStatusChange}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingStaff ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffManagement;